import { RequestTypeEnum } from "@nlc/shared-types";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";

import { useEditRequestFormContext } from "@/features/request/edit/hooks/useEditRequestForm";
import { useGetCurrentProjectId } from "@/hooks/useGetCurrentProjectId.tsx";
import { useIsTemplate } from "@/hooks/useIsTemplate.tsx";
import { useUser } from "@/hooks/useUser.tsx";
import {
  default as apiClient,
  default as authenticatedApi,
} from "@/lib/api/client.ts";

export function useDuplicateTemplate() {
  const { getValues } = useEditRequestFormContext();
  const { requestType } = useIsTemplate();
  const finalRequestType = requestType as
    | RequestTypeEnum.GRAPHIC
    | RequestTypeEnum.VIDEO;

  const { mutateAsync: createDraft } = useCreateDraftMock(finalRequestType);
  const { mutateAsync: saveDraft } = useSaveDraftMock();
  const projectId = useGetCurrentProjectId();
  const navigate = useNavigate();

  async function handleCreateDraft() {
    try {
      const response = await createDraft();
      const requestId = response.requestId;
      const formValues = getValues();

      const parsed = {
        attachmentIds: formValues.attachmentIds,
        briefCreatorType: "single",
        collaboratorIds: formValues.collaboratorIds,
        creativeDirection: formValues.creativeDirection,
        description: formValues.description,
        designTypeId: formValues.designTypeId,
        name: formValues.name,
        preferredDesignerIds: formValues.preferredDesignerIds,
        preferredFileType: formValues.preferredFileType,
        requestType: RequestTypeEnum.GRAPHIC,
        sizesNeeded: formValues.sizesNeeded,
        textForDesign: formValues.textForDesign,
      };

      await saveDraft({ data: parsed, draftId: requestId });

      navigate({
        search: {
          draft_id: requestId,
          "project-id": projectId,
          "request-type": finalRequestType,
        },
        to: "/requests/create",
      });
    } catch (error) {
      console.error("Failed to create or save draft:", error);
    }
  }

  return { handleCreateDraft };
}

function useCreateDraftMock(
  requestType: RequestTypeEnum.GRAPHIC | RequestTypeEnum.VIDEO,
) {
  const projectId = useGetCurrentProjectId();
  const { data: userData } = useUser();

  return useMutation({
    mutationFn: async (): Promise<{ requestId: number }> =>
      apiClient
        .url("/requests")
        .post({ projectId, requestType: requestType })
        .json(),
    mutationKey: [
      "createDraft",
      { projectId: projectId, userId: userData?.user.id },
    ],
  });
}

function useSaveDraftMock() {
  return useMutation({
    mutationFn: async ({ data, draftId }: { data: unknown; draftId: number }) =>
      await authenticatedApi.url(`/requests/${draftId}`).patch(data).json(),
    mutationKey: ["saveDraft"],
  });
}
